<i18n locale="en">
{
  "days": {
    "future": "{date}",
  },
  "actions": {
    "schedule": "Change Schedule",
    "subject": "Update Subject",
    "audience": "Update Subscriber Audience",
    "design": "Edit Design",
    "test-send": "Send test",
    "send": "Send Now"
  },
  "titles": {
    "scheduled": "Sending on",
  },
  "subtitle": "{dateText}"
}
</i18n>

<template>
  <div class="flex-col gap-8">
    <TitleBlock
      v-if="status === 'scheduled'"
      :title="t('titles.scheduled')"
      :subtitle="t('subtitle', { dateText })"
      variant="medium"
    />
    <div class="campaign-controls flex-col gap-3">
      <OwnButton
        v-for="{ icon, label, primary, action: key } of actions"
        :key="key"
        :variant="primary ? 'primary' : 'secondary'"
        :text="label"
        @click="processAction(key)"
      >
        <template v-if="icon" #icon-right>
          <component :is="icon" size="24" />
        </template>
      </OwnButton>
    </div>

    <TestSendEmailDialog
      v-model="showTestSendDialog"
      :template-id="emailTemplateId"
    />
  </div>
</template>

<script>
import {
  PhCalendarBlank,
  PhTextAlignLeft,
  PhUsers,
  PhWrench,
  PhArrowCircleRight,
} from '@phosphor-icons/vue'
import { markRaw, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import TitleBlock from '@/components/typography/TitleBlock.vue'
import { useActiveBrand } from '@/core/store'
import { OwnButton } from '@/ui/OwnButton'
import { mapModel } from '@/utils/computed'
import { formatDateTimeWithTimezone } from '@/utils/date/formatDateTimeWithTimezone'
import { toRouteLocation } from '@/utils/toRouteLocation'
import { useReactiveFeatureFlag } from '@/utils/useFeatureFlags'

import { emailBuilderRouteManager } from '../../../builders/email/emailBuilderRouter'
import { textBuilderRouteManager } from '../../../builders/text/textBuilderRouter'
import TestSendEmailDialog from '../../TestSendEmailDialog.vue'

export default {
  name: 'CampaignControls',
  components: { OwnButton, TestSendEmailDialog, TitleBlock },
  props: {
    /** The ID of the current campaign */
    campaign: { type: String, required: true },

    /** The email template ID for test sends */
    emailTemplateId: { type: String, default: undefined },

    /** The scheduled date for the campaign */
    executeAt: { type: Date, default: null },

    /** The action performed */
    modelValue: { type: String, default: null },

    /** The transport of the current campaign */
    transport: { type: String, default: null },
  },
  emits: ['show-dialog', 'update:modelValue'],
  setup() {
    const { t } = useI18n()
    const { isMultiLocation } = useActiveBrand()
    const { enabled: testSendEnabled } = useReactiveFeatureFlag(
      'hermes.marketing.testSendEmail'
    )

    return {
      isMultiLocation,
      showTestSendDialog: ref(false),
      t,
      testSendEnabled,
    }
  },
  computed: {
    ...mapModel('action'),
    actions() {
      return [
        { action: 'schedule', icon: markRaw(PhCalendarBlank), primary: true },
        ...(this.transport === 'email'
          ? [{ action: 'subject', icon: markRaw(PhTextAlignLeft) }]
          : []),
        ...(this.isMultiLocation
          ? [{ action: 'audience', icon: markRaw(PhUsers) }]
          : []),
        { action: 'design', icon: markRaw(PhWrench) },
        ...(this.testSendEnabled && this.transport === 'email'
          ? [{ action: 'test-send' }]
          : []),
        { action: 'send', icon: markRaw(PhArrowCircleRight) },
      ].map(({ action, ...rest }) => ({
        action,
        label: this.t(`actions.${action}`),
        ...rest,
      }))
    },
    dateText() {
      const { executeAt } = this

      if (!executeAt) return

      return this.t(`days.future`, {
        date: formatDateTimeWithTimezone(executeAt),
      })
    },
    status() {
      return this.executeAt ? 'scheduled' : ''
    },
  },
  methods: {
    async processAction(action) {
      switch (action) {
        case 'schedule':
        case 'subject':
        case 'audience':
        case 'send':
          this.action = action
          this.$emit('show-dialog')
          break
        case 'test-send':
          this.showTestSendDialog = true
          break
        case 'design': {
          const routeManager =
            this.transport === 'sms'
              ? textBuilderRouteManager
              : emailBuilderRouteManager
          const editResourceRoute = await routeManager.editResourcePage(
            this.campaign,
            toRouteLocation(this.$route)
          )

          await this.$router.push(editResourceRoute)
          break
        }
        default:
          throw new Error('Not a valid action')
      }
    },
  },
}
</script>
